import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AddNewNodeIcon, ColorPickerIcon } from './icons';
import { positionNodeActionCapsule } from '../../utils/mindmap/nodeActionCapsule';
import './NodeActionCapsule.css';

const TaskChildIcon = () => (
  <svg className="node-action-capsule__line-icon" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);

const DiscussionIcon = () => (
  <svg className="node-action-capsule__line-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 5h14v10H9l-4 4V5Z" />
    <path d="M8 9h8M8 12h5" />
  </svg>
);

const InfoIcon = () => (
  <svg className="node-action-capsule__line-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v6M12 7.5v.5" />
  </svg>
);

const MoreIcon = () => (
  <svg className="node-action-capsule__line-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

function visibleOcclusions(containerRect) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return [];
  return [...document.querySelectorAll('[data-viewport-occlusion="true"]')]
    .filter((element) => {
      const style = window.getComputedStyle(element);
      return (
        style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number.parseFloat(style.opacity || '1') > 0.05
        && element.getClientRects().length > 0
      );
    })
    .map((element) => element.getBoundingClientRect())
    .map((rect) => ({
      x: rect.left - containerRect.left,
      y: rect.top - containerRect.top,
      width: rect.width,
      height: rect.height,
    }));
}

function CapsuleButton({ action, isVisible, onActivate }) {
  return (
    <button
      type="button"
      className="node-action-capsule__button"
      aria-label={action.label}
      title={action.label}
      tabIndex={isVisible ? 0 : -1}
      onClick={(event) => {
        event.stopPropagation();
        onActivate(action);
      }}
    >
      {action.icon}
    </button>
  );
}

function NodeActionCapsule({
  nodeId,
  nodeLabel,
  anchor,
  container,
  compact = false,
  isDarkMode = false,
  isVisible = true,
  positioningKey,
  canWrite = true,
  canChangeColor = true,
  canDiscuss = false,
  canOpenInfo = true,
  onAddChild,
  onAddTaskChild,
  onChangeColor,
  onOpenDiscussion,
  onOpenInfo,
  onReturnFocus,
  getUsableViewportGeometry,
}) {
  const capsuleRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const actions = useMemo(() => [
    canWrite && {
      id: 'child',
      label: 'Add child node',
      icon: <AddNewNodeIcon />,
      run: onAddChild,
    },
    canWrite && {
      id: 'task-child',
      label: 'Add task node',
      icon: <TaskChildIcon />,
      run: onAddTaskChild,
    },
    canWrite && canChangeColor && {
      id: 'colour',
      label: 'Change color',
      icon: <ColorPickerIcon />,
      run: onChangeColor,
    },
    canDiscuss && {
      id: 'discussion',
      label: 'Discuss',
      icon: <DiscussionIcon />,
      run: onOpenDiscussion,
    },
    canOpenInfo && {
      id: 'info',
      label: 'Info',
      icon: <InfoIcon />,
      run: onOpenInfo,
    },
  ].filter(Boolean), [
    canChangeColor,
    canDiscuss,
    canOpenInfo,
    canWrite,
    onAddChild,
    onAddTaskChild,
    onChangeColor,
    onOpenDiscussion,
    onOpenInfo,
  ]);
  const primaryActions = compact && actions.length > 2 ? actions.slice(0, 2) : actions;
  const overflowActions = compact && actions.length > 2 ? actions.slice(2) : [];

  useEffect(() => {
    setIsOverflowOpen(false);
  }, [nodeId, compact]);

  useEffect(() => {
    if (!isVisible) setIsOverflowOpen(false);
  }, [isVisible]);

  useEffect(() => {
    if (!isOverflowOpen || typeof document === 'undefined') return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!capsuleRef.current?.contains(event.target)) setIsOverflowOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [isOverflowOpen]);

  useLayoutEffect(() => {
    if (!anchor || !container || !capsuleRef.current) return undefined;
    let frameId = null;
    const updatePosition = () => {
      frameId = null;
      const anchorRect = anchor.getBoundingClientRect?.();
      const containerRect = container.getBoundingClientRect?.();
      const capsuleRect = capsuleRef.current?.getBoundingClientRect?.();
      if (!anchorRect || !containerRect || !capsuleRect) return;
      const geometry = getUsableViewportGeometry?.();
      const usableBounds = geometry?.screenBounds || {
        x: 8,
        y: 8,
        width: Math.max(0, containerRect.width - 16),
        height: Math.max(0, containerRect.height - 16),
      };
      const next = positionNodeActionCapsule({
        anchorRect: {
          x: anchorRect.left - containerRect.left,
          y: anchorRect.top - containerRect.top,
          width: anchorRect.width,
          height: anchorRect.height,
        },
        capsuleSize: {
          width: capsuleRect.width,
          height: capsuleRect.height,
        },
        bounds: usableBounds,
        obstacles: geometry?.screenObstacles || visibleOcclusions(containerRect),
      });
      if (next) {
        setPosition({
          ...next,
          overflowDirection: next.y > usableBounds.y + usableBounds.height / 2
            ? 'up'
            : 'down',
        });
      }
    };
    const schedulePosition = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updatePosition);
    };
    schedulePosition();
    const anchorElement = anchor.getDOM?.();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(schedulePosition)
      : null;
    if (anchorElement) observer?.observe(anchorElement);
    observer?.observe(container);
    window.addEventListener('resize', schedulePosition);
    window.visualViewport?.addEventListener?.('resize', schedulePosition);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener('resize', schedulePosition);
      window.visualViewport?.removeEventListener?.('resize', schedulePosition);
    };
  }, [
    actions.length,
    anchor,
    compact,
    container,
    getUsableViewportGeometry,
    positioningKey,
  ]);

  if (!actions.length) return null;

  const activate = (action) => {
    setIsOverflowOpen(false);
    action.run?.(nodeId);
  };
  const colors = isDarkMode
    ? {
        background: 'rgba(31, 41, 51, 0.96)',
        border: '#64748b',
        foreground: '#f8fafc',
        hover: '#374151',
      }
    : {
        background: 'rgba(255, 255, 255, 0.97)',
        border: '#cbd5e1',
        foreground: '#111827',
        hover: '#eef2f7',
      };

  return (
    <div
      ref={capsuleRef}
      className="node-action-capsule"
      role="toolbar"
      aria-label={`Actions for ${(nodeLabel || '').trim() || 'untitled node'}`}
      data-testid="node-action-capsule"
      data-action-node-id={nodeId}
      data-corner={position?.corner || 'left'}
      data-vertical={position?.vertical || 'above'}
      data-overflow-direction={position?.overflowDirection || 'down'}
      data-visible={isVisible ? 'true' : 'false'}
      data-export-ignore="true"
      aria-hidden={!isVisible}
      style={{
        left: `${position?.x || 0}px`,
        top: `${position?.y || 0}px`,
        visibility: position ? 'visible' : 'hidden',
        '--node-action-background': colors.background,
        '--node-action-border': colors.border,
        '--node-action-foreground': colors.foreground,
        '--node-action-hover': colors.hover,
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          setIsOverflowOpen(false);
          onReturnFocus?.();
        }
      }}
    >
      {primaryActions.map((action) => (
        <CapsuleButton
          key={action.id}
          action={action}
          isVisible={isVisible}
          onActivate={activate}
        />
      ))}
      {overflowActions.length > 0 && (
        <div className="node-action-capsule__overflow-wrap">
          <button
            type="button"
            className="node-action-capsule__button"
            aria-label="More node actions"
            title="More node actions"
            aria-expanded={isOverflowOpen}
            aria-haspopup="menu"
            tabIndex={isVisible ? 0 : -1}
            onClick={(event) => {
              event.stopPropagation();
              setIsOverflowOpen((open) => !open);
            }}
          >
            <MoreIcon />
          </button>
          {isOverflowOpen && (
            <div
              className="node-action-capsule__overflow"
              role="menu"
              aria-label="More node actions"
            >
              {overflowActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  role="menuitem"
                  onClick={(event) => {
                    event.stopPropagation();
                    activate(action);
                  }}
                >
                  <span className="node-action-capsule__menu-icon">{action.icon}</span>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NodeActionCapsule;
